/**
 * BarDrilldownCustomWidget — vertical-column measures + drilldown, plain
 * React/CSS, no Highcharts. A from-scratch alternative to
 * BarDrilldownWidget.tsx (which hit Highcharts-integration issues: a duplicate
 * context-button menu, and the chart collapsing/not recovering when sibling
 * content resizes its container) — kept side by side so both can be compared.
 *
 * chart_type: "bar_drilldown_custom". Own drill-state stack replaces
 * Highcharts' native drilldown module; the columns are plain DOM boxes with
 * percentage heights, so there is no canvas/SVG sizing to keep in sync with the
 * container — it reflows with normal CSS layout like any other content.
 *
 * ── Design notes (the non-obvious calls) ──────────────────────────────────
 *
 * 1. THE TOP-LEVEL MEASURES ARE NOT A PARTITION, and the chart must not imply
 *    they are. In the live payload they are four different slices of different
 *    populations:
 *      Total Cases           27,191  the universe
 *      PA/Precert Denied        919  PA/Precert cases whose task status = denied
 *      With Denial Tasks          0  ┐ these two partition PA/Precert cases
 *      Without Denial Tasks  12,016  ┘ (0 + 12,016 = metadata.pa_precert_cases)
 *    They do not sum to the total, and they are deliberately NOT re-ordered or
 *    re-grouped here (`preserve_category_order: true`). What makes them
 *    intelligible instead is note 2.
 *
 * 2. The readout surfaces each measure's DEFINITION, taken from
 *    `metadata.technical_detail_rows` (which supplies one per top-level
 *    measure, matched on `measure` === the point name). That is the honest way
 *    to tell the reader that "Without Denial Tasks" counts PA/Precert cases
 *    while "Total Cases" counts everything — a chart alone cannot say it, and
 *    inventing visual grouping for it would be guesswork.
 *
 * 3. No funnel/step-conversion logic. An earlier revision drew the top level as
 *    a funnel with stage-to-stage conversion, which suited a payload whose
 *    stages were nested and monotonically decreasing. This payload is not
 *    monotonic (27,191 → 919 → 0 → 12,016), so a "conversion" between adjacent
 *    columns would be meaningless. Shares are quoted against a single named
 *    denominator instead (note 4).
 *
 * 4. Share is "% of Total Cases" for every top-level measure, and "% of <the
 *    bar we opened>" inside a drilldown. Total Cases is used uniformly at the
 *    top rather than picking a per-measure denominator (PA/Precert for the
 *    with/without pair, say), because choosing one per row means inferring
 *    semantics from label text — exactly the guesswork that broke across the
 *    last two payload revisions. The definition line carries the nuance.
 *
 * 5. Every bar shares ONE scale and ONE hue; only the universe measure
 *    (`/^total\b/i`) is drawn in the de-emphasis gray, since it is the
 *    denominator rather than a peer measurement. A value-ramp is deliberately
 *    not used — these categories are nominal, so colouring them by size would
 *    double-encode bar length as hue.
 *
 * 6. A measured zero renders a lighter 2px stub plus an explicit "0", so it
 *    reads as counted-and-none rather than missing — which matters here,
 *    because "With Denial Tasks = 0" is the card's headline finding.
 *
 * 7. Drilldowns are only offered when the target series actually has rows. In
 *    this payload `with_denial_task_cases` is `data: []`, so nothing is
 *    clickable and the widget is effectively single-level. That series is also
 *    `type: "table"` with its own `columns`, so a table drilldown is rendered as
 *    a table rather than as bars — see `renderDrilldownTable`. NOTE: that path
 *    cannot be exercised against the live payload while `data` is empty, so the
 *    row shape is handled defensively (object keyed by column, or positional
 *    array) rather than assumed.
 */

import { useMemo, useState } from "react";

import { toSafeNumber } from "src/charts/data/transformers";
import "./BarDrilldownCustomWidget.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type BarPoint = {
  x?: string;
  name?: string;
  y?: unknown;
  drilldown?: string;
};

type BarSeries = {
  name?: string;
  type?: string;
  data?: BarPoint[];
};

type DrilldownSeries = {
  id?: string;
  name?: string;
  /** "column" (default) or "table" — a table level lists rows, not bars. */
  type?: string;
  /** Column order for a `type: "table"` level. */
  columns?: string[];
  data?: (BarPoint | unknown[] | Record<string, unknown>)[];
};

type Headline = { label?: string; value?: string; subtitle?: string };
type Status = { code?: string; label?: string; description?: string };
type TechnicalDetailRow = {
  measure?: string;
  cases?: number;
  definition?: string;
};
type DenialTaskDetail = {
  case_id?: string;
  task_status?: string;
};

type Metadata = {
  headline?: Headline;
  status?: Status;
  total_cases?: number;
  technical_detail_rows?: TechnicalDetailRow[];
  with_denial_task_details?: DenialTaskDetail[];
};

type BarDrilldownData = {
  series?: BarSeries[];
  drilldown?: { series?: DrilldownSeries[] };
};

type BarDrilldownCustomWidgetProps = {
  isLoading: boolean;
  data: unknown;
};

/** One resolved column, ready to draw and describe. */
type ColumnView = {
  key: string;
  /** Raw payload name — used for keys, the definition lookup and the total test. */
  name: string;
  /** Humanized name for display. */
  label: string;
  y: number;
  drilldownId?: string;
  /** True for the universe measure, drawn in the de-emphasis gray. */
  isContext: boolean;
  /** Definition of this measure, when the payload supplies one. */
  definition?: string;
};

/** One entry on the drill stack. Carries the value so children can show a
 *  share of the bar they came from, and the label for the breadcrumb. */
type DrillStep = { id: string; label: string; value: number };

type ViewMode = "chart" | "table";

// ─── Palette ──────────────────────────────────────────────────────────────────

// Reserved status tokens — never reused as a series color.
const STATUS_STYLES: Record<string, { color: string; icon: string }> = {
  good: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  ok: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  healthy: { color: "#0ca30c", icon: "bi-check-circle-fill" },
  warning: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  no_new_data: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  no_denial_tasks: { color: "#fab219", icon: "bi-exclamation-triangle-fill" },
  no_denial_conversion: {
    color: "#fab219",
    icon: "bi-exclamation-triangle-fill",
  },
  serious: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  degraded: { color: "#ec835a", icon: "bi-exclamation-circle-fill" },
  critical: { color: "#d03b3b", icon: "bi-x-circle-fill" },
  error: { color: "#d03b3b", icon: "bi-x-circle-fill" },
};
const DEFAULT_STATUS_STYLE = STATUS_STYLES.warning!;

// Drilldown series that list rows rather than magnitudes, for payloads that
// don't mark them with `type: "table"`. The live payload DOES mark
// `with_denial_task_cases` as a table, so this is a fallback, not the primary
// signal — see `isTableLevel`.
const TABLE_SERIES_IDS = new Set(["with_denial_task_cases"]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const unwrap = (
  raw: unknown,
): { data?: BarDrilldownData; metadata?: Metadata } => {
  if (!raw || typeof raw !== "object") return {};
  return raw as { data?: BarDrilldownData; metadata?: Metadata };
};

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const formatNum = (val: unknown): string =>
  isNumeric(val) ? val.toLocaleString() : String(val ?? "–");

const formatPct = (value: number): string => `${value.toFixed(1)}%`;

// The universe measure — the denominator, not a peer of the other columns.
const isContextName = (name: string) => /^total\b/i.test(name.trim());

// Keys can arrive as raw lowercase snake_case (task statuses like
// `pending_approval`); anything already carrying a capital ("PA/Precert
// Denied", "Total Cases") is display-ready and must be left alone.
const humanize = (raw: string): string => {
  const value = raw.trim();
  if (!value || /[A-Z]/.test(value)) return value;
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Clean, whole-number axis ceilings (0 / 10,000 / 20,000 / 30,000) so values are
 * readable off the grid. Steps snap to 1/2/5/10 × a power of ten and are forced
 * to integers — these are case counts, so a 2.5 tick would be meaningless.
 * axisMax rounds *up* to a whole step so the tallest bar can never overflow the
 * top gridline.
 */
const buildScale = (
  max: number,
  targetTicks = 4,
): { axisMax: number; ticks: number[] } => {
  if (!(max > 0)) return { axisMax: 1, ticks: [0] };
  const rawStep = max / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  const snapped =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = Math.max(1, Math.round(snapped * magnitude));
  const axisMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= axisMax; value += step) ticks.push(value);
  return { axisMax, ticks };
};

/** Reads one cell of a `type: "table"` drilldown row. The live payload returns
 *  `data: []` for the only table level, so both shapes the API family uses
 *  elsewhere are handled rather than one being assumed. */
const tableCell = (row: unknown, column: string, index: number): string => {
  if (Array.isArray(row)) {
    const value = row[index];
    return value === undefined || value === null ? "—" : String(value);
  }
  if (row && typeof row === "object") {
    const value = (row as Record<string, unknown>)[column];
    return value === undefined || value === null ? "—" : String(value);
  }
  return "—";
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function BarDrilldownCustomWidget({
  isLoading,
  data,
}: BarDrilldownCustomWidgetProps) {
  const { data: chartData, metadata } = useMemo(() => unwrap(data), [data]);
  const drilldownSeriesList = useMemo(
    () => chartData?.drilldown?.series ?? [],
    [chartData],
  );

  // Navigation stack — empty means the top-level measures are showing.
  const [drillPath, setDrillPath] = useState<DrillStep[]>([]);
  const [view, setView] = useState<ViewMode>("chart");
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const topSeries = chartData?.series?.[0];
  const detailRows = useMemo(
    () => metadata?.technical_detail_rows ?? [],
    [metadata],
  );

  // measure name → definition, so a column can explain what population it
  // counts (see design note 2).
  const definitionByMeasure = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of detailRows) {
      if (row.measure && row.definition) map.set(row.measure, row.definition);
    }
    return map;
  }, [detailRows]);

  const currentStep = drillPath[drillPath.length - 1];
  const currentSeries = currentStep
    ? drilldownSeriesList.find((series) => series.id === currentStep.id)
    : undefined;
  // A drilldown level that lists rows rather than magnitudes. Detected from
  // `type: "table"` (how the payload marks it), OR from a known table series id
  // as a belt-and-braces fallback for a payload that omits `type`. The id check
  // is what the DEV-4012 work used (`isDenialCaseDrilldown`); keeping it here
  // preserves that guard in one place instead of gating each render branch.
  const isTableLevel =
    currentSeries?.type === "table" ||
    (!!currentStep && TABLE_SERIES_IDS.has(currentStep.id));

  const resolvePoints = useMemo(() => {
    return (
      raw: (BarPoint | unknown[] | Record<string, unknown>)[] | undefined,
    ): ColumnView[] =>
      (raw ?? []).map((point, index) => {
        // Highcharts also accepts the tuple form [name, value]; the live payload
        // uses objects, but the tuple path is kept so a shape change doesn't
        // blank the widget.
        if (Array.isArray(point)) {
          const name = String(point[0] ?? "");
          return {
            key: name || String(index),
            name,
            label: humanize(name),
            y: toSafeNumber(point[1]),
            isContext: isContextName(name),
          };
        }
        const p = point as BarPoint;
        const name = String(p.x ?? p.name ?? "");
        const target = p.drilldown
          ? drilldownSeriesList.find((series) => series.id === p.drilldown)
          : undefined;
        // Only offer a drilldown that actually has rows — "With Denial Tasks"
        // points at `with_denial_task_cases`, which is `data: []` in the live
        // payload, and a click landing on an empty level is worse than no
        // affordance at all.
        const hasDrilldown = !!target && (target.data?.length ?? 0) > 0;
        const definition = definitionByMeasure.get(name);
        return {
          key: name || String(index),
          name,
          label: humanize(name),
          y: toSafeNumber(p.y),
          isContext: isContextName(name),
          ...(definition ? { definition } : {}),
          ...(hasDrilldown && p.drilldown ? { drilldownId: p.drilldown } : {}),
        };
      });
  }, [drilldownSeriesList, definitionByMeasure]);

  const columns = useMemo(
    () =>
      resolvePoints(
        currentStep
          ? isTableLevel
            ? []
            : currentSeries?.data
          : topSeries?.data,
      ),
    [resolvePoints, currentStep, isTableLevel, currentSeries, topSeries],
  );

  const levelName = currentStep
    ? (currentSeries?.name ?? currentStep.label)
    : (topSeries?.name ?? "");

  // Denominator for every share on this level (see design note 4).
  const shareBase = currentStep
    ? currentStep.value
    : (columns.find((point) => point.isContext)?.y ??
      metadata?.total_cases ??
      0);
  const shareBaseLabel = currentStep
    ? currentStep.label
    : (columns.find((point) => point.isContext)?.label ?? "Total Cases");

  const maxValue = columns.reduce(
    (max, point) => (point.y > max ? point.y : max),
    0,
  );
  const { axisMax, ticks } = useMemo(() => buildScale(maxValue), [maxValue]);

  const drillInto = (point: ColumnView) => {
    if (!point.drilldownId) return;
    setDrillPath((path) => [
      ...path,
      { id: point.drilldownId!, label: point.label, value: point.y },
    ]);
    setActiveKey(null);
  };

  const goToDepth = (depth: number) => {
    setDrillPath((path) => path.slice(0, depth));
    setActiveKey(null);
  };

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100 w-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const headline = metadata?.headline;
  const status = metadata?.status;
  const statusStyle = status?.code
    ? (STATUS_STYLES[status.code] ?? DEFAULT_STATUS_STYLE)
    : null;

  if ((topSeries?.data?.length ?? 0) === 0) {
    return <p className="text-muted small mb-0">No data available.</p>;
  }

  const activePoint = columns.find((point) => point.key === activeKey);

  const shareOf = (point: ColumnView): string => {
    if (shareBase <= 0) return "";
    // The universe measure is 100% of itself — saying so is noise.
    if (point.isContext && !currentStep) return "";
    return `${formatPct((point.y / shareBase) * 100)} of ${shareBaseLabel}`;
  };

  // Row source for a table level. The payload exposes the rows in TWO places —
  // `drilldown.series[].data` and `metadata.with_denial_task_details` — and both
  // are `[]` in every response observed so far, so which one the backend
  // actually fills is unverified. The DEV-4012 work read the metadata list; this
  // component reads the series' own data. Prefer the series (the structural
  // home) and fall back to metadata, so the table populates either way rather
  // than silently staying empty.
  const metaDetailRows = metadata?.with_denial_task_details ?? [];
  const seriesRows = currentSeries?.data ?? [];
  const tableRows: unknown[] = !isTableLevel
    ? []
    : seriesRows.length > 0
      ? seriesRows
      : metaDetailRows;

  // Columns as declared by the series; otherwise derived from the first row's
  // keys so a metadata-sourced table still gets headers.
  const firstRow = tableRows[0];
  const derivedColumns =
    firstRow && typeof firstRow === "object" && !Array.isArray(firstRow)
      ? Object.keys(firstRow as Record<string, unknown>)
      : [];
  const tableColumns = currentSeries?.columns?.length
    ? currentSeries.columns
    : derivedColumns;

  return (
    <div className="bar-drilldown-custom">
      {/* The status banner is the richer signal, so the headline subtitle only
          appears when there is no status — otherwise the card states nearly the
          same fact twice. The headline *label* is dropped entirely: it repeats
          the card title WidgetRenderer already renders above this component. */}
      {status && statusStyle ? (
        <div className="bar-drilldown-custom__status" role="status">
          <i
            className={`bi ${statusStyle.icon}`}
            style={{ color: statusStyle.color }}
            aria-hidden="true"
          />
          <div className="bar-drilldown-custom__status-text">
            {status.label && (
              <span className="bar-drilldown-custom__status-label">
                {status.label}
              </span>
            )}
            {status.description && (
              <span className="bar-drilldown-custom__status-desc">
                {status.description}
              </span>
            )}
          </div>
        </div>
      ) : headline?.subtitle ? (
        <p className="bar-drilldown-custom__subtitle">{headline.subtitle}</p>
      ) : null}

      <div className="bar-drilldown-custom__toolbar">
        <nav
          className="bar-drilldown-custom__breadcrumb"
          aria-label="Drilldown path"
        >
          {drillPath.length === 0 ? (
            <span className="bar-drilldown-custom__crumb-current">
              {levelName}
            </span>
          ) : (
            <>
              <button type="button" onClick={() => goToDepth(0)}>
                {topSeries?.name ?? "All"}
              </button>
              {drillPath.map((step, index) => {
                const isLast = index === drillPath.length - 1;
                return (
                  <span key={step.id}>
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                    {isLast ? (
                      <span className="bar-drilldown-custom__crumb-current">
                        {levelName}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => goToDepth(index + 1)}
                      >
                        {step.label}
                      </button>
                    )}
                  </span>
                );
              })}
            </>
          )}
        </nav>

        {/* A table level has no chart form, so the toggle would be a lie. */}
        {!isTableLevel && (
          <div
            className="bar-drilldown-custom__views"
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={`bar-drilldown-custom__view-btn${
                view === "chart"
                  ? " bar-drilldown-custom__view-btn--active"
                  : ""
              }`}
              aria-pressed={view === "chart"}
              onClick={() => setView("chart")}
            >
              <i className="bi bi-bar-chart-fill" aria-hidden="true" />
              Chart
            </button>
            <button
              type="button"
              className={`bar-drilldown-custom__view-btn${
                view === "table"
                  ? " bar-drilldown-custom__view-btn--active"
                  : ""
              }`}
              aria-pressed={view === "table"}
              onClick={() => setView("table")}
            >
              <i className="bi bi-table" aria-hidden="true" />
              Table
            </button>
          </div>
        )}
      </div>

      {isTableLevel ? (
        // A `type: "table"` drilldown lists rows, not magnitudes.
        tableRows.length === 0 ? (
          <div className="bar-drilldown-custom__empty">
            No rows for {levelName || "this level"}.
          </div>
        ) : (
          <div className="bar-drilldown-custom__table-wrap">
            <table className="bar-drilldown-custom__table">
              <thead>
                <tr>
                  {tableColumns.map((column) => (
                    <th scope="col" key={column}>
                      {humanize(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {tableColumns.map((column, columnIndex) => (
                      <td key={column}>
                        {tableCell(row, column, columnIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : columns.length === 0 ? (
        <div className="bar-drilldown-custom__empty">
          No breakdown available for {levelName || "this level"}.
        </div>
      ) : view === "table" ? (
        <div className="bar-drilldown-custom__table-wrap">
          <table className="bar-drilldown-custom__table">
            <thead>
              <tr>
                <th scope="col">{currentStep ? "Category" : "Measure"}</th>
                <th scope="col" className="bar-drilldown-custom__table-num">
                  Cases
                </th>
                <th scope="col" className="bar-drilldown-custom__table-num">
                  {shareBase > 0 ? `% of ${shareBaseLabel}` : "Share"}
                </th>
                {/* Definitions only exist for the top-level measures. */}
                {!currentStep && definitionByMeasure.size > 0 && (
                  <th scope="col">Definition</th>
                )}
              </tr>
            </thead>
            <tbody>
              {columns.map((point) => (
                <tr
                  key={point.key}
                  className={`bar-drilldown-custom__table-row${
                    point.drilldownId
                      ? " bar-drilldown-custom__table-row--drillable"
                      : ""
                  }`}
                >
                  <td>
                    {point.drilldownId ? (
                      <button
                        type="button"
                        className="bar-drilldown-custom__table-btn"
                        onClick={() => drillInto(point)}
                      >
                        {point.label}
                        <i
                          className="bi bi-chevron-right ms-1"
                          style={{ fontSize: 9 }}
                          aria-hidden="true"
                        />
                      </button>
                    ) : (
                      point.label
                    )}
                  </td>
                  <td className="bar-drilldown-custom__table-num">
                    {formatNum(point.y)}
                  </td>
                  <td className="bar-drilldown-custom__table-num">
                    {shareOf(point)
                      ? formatPct((point.y / shareBase) * 100)
                      : "—"}
                  </td>
                  {!currentStep && definitionByMeasure.size > 0 && (
                    <td>{point.definition ?? "—"}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="bar-drilldown-custom__readout" aria-live="polite">
            {activePoint ? (
              <>
                <span className="bar-drilldown-custom__readout-top">
                  <span className="bar-drilldown-custom__readout-name">
                    {activePoint.label}
                  </span>
                  <span className="bar-drilldown-custom__readout-meta">
                    {formatNum(activePoint.y)} cases
                    {shareOf(activePoint) ? ` · ${shareOf(activePoint)}` : ""}
                  </span>
                  {activePoint.drilldownId && (
                    <span className="bar-drilldown-custom__readout-hint">
                      Click to break down
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                    </span>
                  )}
                </span>
                {/* What population this measure counts — the reason the columns
                    can't be read as parts of one whole. */}
                {activePoint.definition && (
                  <span className="bar-drilldown-custom__readout-def">
                    {activePoint.definition}
                  </span>
                )}
              </>
            ) : (
              <span className="bar-drilldown-custom__readout-idle">
                {columns.length}{" "}
                {currentStep
                  ? `categor${columns.length === 1 ? "y" : "ies"} in ${
                      currentStep.label
                    } (${formatNum(currentStep.value)})`
                  : `measure${columns.length === 1 ? "" : "s"}`}
                {" — hover a column for its definition"}
              </span>
            )}
          </div>

          <div className="bar-drilldown-custom__plot">
            <div className="bar-drilldown-custom__yaxis" aria-hidden="true">
              <div className="bar-drilldown-custom__yaxis-cap" />
              <div className="bar-drilldown-custom__yaxis-scale">
                {ticks.map((tick) => (
                  <span
                    key={tick}
                    className="bar-drilldown-custom__tick"
                    style={{
                      bottom: `${axisMax > 0 ? (tick / axisMax) * 100 : 0}%`,
                    }}
                  >
                    {tick.toLocaleString()}
                  </span>
                ))}
              </div>
              <div className="bar-drilldown-custom__yaxis-foot" />
            </div>

            <div className="bar-drilldown-custom__scroll">
              {/* Keyed on the level so the bars remount and replay their grow
                  animation when the reader drills in or out. */}
              <div
                className="bar-drilldown-custom__track"
                key={currentStep?.id ?? "root"}
              >
                <div
                  className="bar-drilldown-custom__gridlines"
                  aria-hidden="true"
                >
                  {ticks.map((tick) => (
                    <span
                      key={tick}
                      className={`bar-drilldown-custom__gridline${
                        tick === 0
                          ? " bar-drilldown-custom__gridline--base"
                          : ""
                      }`}
                      style={{
                        bottom: `${axisMax > 0 ? (tick / axisMax) * 100 : 0}%`,
                      }}
                    />
                  ))}
                </div>

                {columns.map((point) => {
                  const heightPct = axisMax > 0 ? (point.y / axisMax) * 100 : 0;
                  const isZero = point.y <= 0;
                  const share = shareOf(point);
                  return (
                    <button
                      key={point.key}
                      type="button"
                      className={`bar-drilldown-custom__band${
                        point.drilldownId
                          ? " bar-drilldown-custom__band--drillable"
                          : ""
                      }`}
                      // Never `disabled`: a disabled button can't take focus,
                      // and focus is how a keyboard reader reaches the readout's
                      // full label and definition.
                      aria-disabled={point.drilldownId ? undefined : true}
                      aria-label={`${point.label}: ${formatNum(point.y)} cases${
                        share ? `, ${share}` : ""
                      }${point.definition ? `. ${point.definition}` : ""}${
                        point.drilldownId ? ". Break down." : ""
                      }`}
                      onClick={() => drillInto(point)}
                      onMouseEnter={() => setActiveKey(point.key)}
                      onMouseLeave={() =>
                        setActiveKey((key) => (key === point.key ? null : key))
                      }
                      onFocus={() => setActiveKey(point.key)}
                      onBlur={() =>
                        setActiveKey((key) => (key === point.key ? null : key))
                      }
                    >
                      <span className="bar-drilldown-custom__cap">
                        {formatNum(point.y)}
                      </span>
                      <span className="bar-drilldown-custom__barwrap">
                        <span
                          className={`bar-drilldown-custom__bar${
                            point.isContext
                              ? " bar-drilldown-custom__bar--context"
                              : ""
                          }${isZero ? " bar-drilldown-custom__bar--zero" : ""}`}
                          // A nonzero value never collapses to nothing (min
                          // 3px), so 919 beside 27,191 stays visible; a true
                          // zero gets the 2px stub.
                          style={{
                            height: isZero ? "2px" : `max(3px, ${heightPct}%)`,
                          }}
                        />
                      </span>
                      <span className="bar-drilldown-custom__cat">
                        <span className="bar-drilldown-custom__cat-text">
                          {point.label}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {detailRows.length > 0 && (
        <div className="bar-drilldown-custom__details">
          <button
            type="button"
            className="bar-drilldown-custom__details-toggle"
            aria-expanded={showDetails}
            onClick={() => setShowDetails((value) => !value)}
          >
            <i
              className={`bi bi-chevron-${showDetails ? "down" : "right"}`}
              aria-hidden="true"
            />
            <span>Technical details</span>
          </button>

          {showDetails && (
            <table className="bar-drilldown-custom__details-table">
              <thead>
                <tr>
                  <th scope="col">Measure</th>
                  <th scope="col">Cases</th>
                  <th scope="col">Definition</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row, index) => (
                  <tr key={row.measure ?? index}>
                    <td>{row.measure ?? "—"}</td>
                    <td>{formatNum(row.cases)}</td>
                    <td>{row.definition ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
